import  { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Dropdown, DropdownButton, Form, Button } from "react-bootstrap";
import keyboardIcon from "../../../../assets/images/keyboard.svg";
import Keyboard from "react-simple-keyboard";
import Constants from "../../../Variables/Constants";
import axios from "axios";
import "react-simple-keyboard/build/css/index.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/Personalize.css";
import "../css/HebrewKeyboard.css";
import "../../ProductPage.css";
import { fontFamilyList } from "../components/Customize.constant";

const createDefaultTextObject = (id = 1, canvasWidth = 800, canvasHeight = 600,color_code ,color_name,code) => ({
  id,
  text: "",
  fontFamily: "Baziporen, sans-serif",
  fontSize: 48,
  color: "#FFD700",
  shape: "straight",
  letterSpacing: 0,
  lineHeight: 1,
  radius: 0,
  x: canvasWidth / 2,
  y: canvasHeight / 2,
  rotation: 0,
  isActive: true,
  textAlign: "left", // Added this default property
  color: color_code ,
  color_name:color_name,
  code:code 
});

const Personalize = ({
  textObjects = [],
  setTextObjects,
  setTotalLetterCount,
  setTotalTextPrice,
  canvasHeight = 600,
  canvasWidth = 800,
  colorList,
  captureSnapshot = () => {},
}) => {
  // refs
  const {color_code ,color_name,code }=colorList?.[0] || {}
  const keyboardRef = useRef(null);
  const textareaRef = useRef(null);
  const timersRef = useRef(new Set());
  // local state
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [keyboardTheme, setKeyboardTheme] = useState("light");
  const [isShift, setIsShift] = useState(false);
  const [isRtl, setIsRtl] = useState(true);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isHebrewMode, setIsHebrewMode] = useState(true);
  const [keyboardLayout, setKeyboardLayout] = useState("default");

  // Ensure component always has at least one text object and select first if none active
  useEffect(() => {
    if (setTextObjects) {
      let needsUpdate = false;
      let newTextObjects = [...(textObjects || [])];
      
      if (newTextObjects.length === 0) {
        const def = createDefaultTextObject(1, canvasWidth, canvasHeight,color_code ,color_name,code);
        newTextObjects = [def];
        needsUpdate = true;
      }
      
      if (newTextObjects.length === 1 && !newTextObjects[0].isActive) {
        newTextObjects[0].isActive = true;
        needsUpdate = true;
      }
      
      if (newTextObjects.length > 0 && !newTextObjects.some(obj => obj.isActive)) {
        newTextObjects[0].isActive = true;
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        setTextObjects(newTextObjects);
      }
    }
  }, [textObjects, setTextObjects, canvasWidth, canvasHeight]);

  // Set RTL based on Hebrew mode
  useEffect(() => {
    setIsRtl(isHebrewMode);
  }, [isHebrewMode]);

  // active object from real state
  const activeTextObject = useMemo(() => {
    if (!textObjects || textObjects.length === 0) return createDefaultTextObject(1, canvasWidth, canvasHeight,color_code ,color_name,code);
    return textObjects?.find((t) => t && t.isActive) || textObjects[0];
  }, [textObjects, canvasWidth, canvasHeight]);

  // update totals
  useEffect(() => {
    if (setTotalLetterCount) {
      const totalLetters = textObjects.reduce((acc, obj) => acc + (obj.text ? obj.text.replace(/\s+/g, "").length : 0), 0);
      setTotalLetterCount(totalLetters);
    }
    if (setTotalTextPrice) {
      const totalPrice = textObjects.reduce((acc, obj) => {
        const len = obj.text ? obj.text.replace(/\s+/g, "").replaceAll("\n", "").length : 0;
        return acc + len * 1.75;
      }, 0);
      setTotalTextPrice(totalPrice);
    }
  }, [textObjects, setTotalLetterCount, setTotalTextPrice]);

  // safe selection-range helper
  const safeSetSelectionRange = useCallback((pos) => {
    try {
      const ta = textareaRef.current;
      if (!ta) return;
      if (document.activeElement !== ta) ta.focus();
      const len = ta.value ? ta.value.length : 0;
      const p = Math.max(0, Math.min(pos, len));
      ta.setSelectionRange(p, p);
    } catch (err) {
      // console.warn("selection error", err);
    }
  }, []);

  // timers management
  const setTimer = (fn, delay = 0) => {
    const id = window.setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, delay);
    timersRef.current.add(id);
    return id;
  };

  useEffect(() => {
    return () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current.clear();
    };
  }, []);

  // helper: update active object
  const updateActiveTextObject = useCallback(
    (updates) => {
      if (!setTextObjects) return;
      setTextObjects((prev) => {
        const next = prev.map((obj) => (obj.isActive ? { ...obj, ...updates } : obj));
        return next;
      });
    },
    [setTextObjects]
  );

  // textarea change
  const handleTextareaChange = (e) => {
    const value = e.target.value || "";
    const cursorPos = e.target.selectionStart ?? 0;
    updateActiveTextObject({ text: value });
    setCursorPosition(cursorPos);
    setTimer(() => safeSetSelectionRange(cursorPos), 0);
    setTimer(() => captureSnapshot(), 100);
  };

  const handleTextareaSelect = (e) => {
    setCursorPosition(e.target.selectionStart ?? 0);
  };

  // ensure cursor updates when active text or cursorPosition changes
  useEffect(() => {
    safeSetSelectionRange(cursorPosition ?? 0);
  }, [activeTextObject?.text, cursorPosition, safeSetSelectionRange]);

  // font change
  const handleFontFamilyChange = useCallback((font) => {
    updateActiveTextObject({ fontFamily: font });
    setTimer(() => captureSnapshot(), 100);
  }, [updateActiveTextObject, captureSnapshot]);

  const toggleKeyboard = useCallback(() => {
    setShowKeyboard((s) => !s);
  }, []);

  const increaseFontSize = useCallback(() => {
    updateActiveTextObject({ fontSize: (activeTextObject?.fontSize || 48) + 2 });
    setTimer(() => captureSnapshot(), 100);
  }, [activeTextObject?.fontSize, updateActiveTextObject, captureSnapshot]);

  const decreaseFontSize = useCallback(() => {
    updateActiveTextObject({ fontSize: Math.max((activeTextObject?.fontSize || 48) - 2, 8) });
    setTimer(() => captureSnapshot(), 100);
  }, [activeTextObject?.fontSize, updateActiveTextObject, captureSnapshot]);

  // add text object
  const addTextObject = useCallback(() => {
    if (!setTextObjects) return;
    setTextObjects((prev = []) => {
      const newId = prev?.length > 0 ? Math.max(...prev.map((p) => p.id)) + 1 : 1;
      const newObj = createDefaultTextObject(newId, canvasWidth, canvasHeight,color_code ,color_name,code);
      newObj.y = canvasHeight / 2 + prev?.length * 60;
      const deactivated = prev.map((p) => ({ ...p, isActive: false }));
      return [...deactivated, newObj];
    });
    setTimer(() => captureSnapshot(), 100);
  }, [setTextObjects, canvasHeight, canvasWidth, captureSnapshot]);

  const deleteTextObject = useCallback((id) => {
    if (!setTextObjects) return;
    setTextObjects((prev = []) => {
      if (prev.length <= 1) {
        alert("At least one text object is required");
        return prev;
      }
      const next = prev.filter((p) => p.id !== id);
      if (next.length > 0) {
        next[0].isActive = true;
      }
      return next;
    });
    setTimer(() => captureSnapshot(), 100);
  }, [setTextObjects, captureSnapshot]);

  const switchTextObject = useCallback((id) => {
    if (!setTextObjects) return;
    setTextObjects((prev = []) => prev.map((p) => ({ ...p, isActive: p.id === id })));
  }, [setTextObjects]);

  const handleClearText = useCallback(() => {
    updateActiveTextObject({
      text: "",
      fontFamily: "Baziporen, sans-serif",
      fontSize: 48,
      color: "#FFD700",
      shape: "straight",
      letterSpacing: 0,
      lineHeight: 1,
      radius: 0,
      rotation: 0,
      textAlign: "left",
    });
    setCursorPosition(0);
    setTimer(() => captureSnapshot(), 100);
  }, [updateActiveTextObject, captureSnapshot]);

  const handleClearAll = useCallback(() => {
    if (!setTextObjects) return;
    const def = createDefaultTextObject(1, canvasWidth, canvasHeight,color_code ,color_name,code);
    setTextObjects([def]);
    setCursorPosition(0);
    setTimer(() => captureSnapshot(), 100);
  }, [setTextObjects, canvasWidth, canvasHeight, captureSnapshot]);

  const toggleLanguageMode = useCallback(() => {
    setIsHebrewMode((prev) => !prev);
    setIsRtl((prev) => !prev);
    setIsShift(false);
    setKeyboardLayout("default");
  }, []);

  // Keyboard onKeyPress handler
  const onKeyPress = useCallback((button) => {
    setActiveKey(button);
    setTimer(() => setActiveKey(null), 200);

    const ta = textareaRef.current;
    const currentText = activeTextObject?.text ?? "";
    const start = ta ? ta.selectionStart : (cursorPosition || 0);
    const end = ta ? ta.selectionEnd : start;

    const applyTextChange = (newText, newCursorPos) => {
      updateActiveTextObject({ text: newText });
      setCursorPosition(newCursorPos);
      setTimer(() => safeSetSelectionRange(newCursorPos), 0);
      setTimer(() => captureSnapshot(), 100);
    };

    // Handle special keys
    if (button === "{shift}" || button === "{lock}") {
      setIsShift((s) => !s);
      setKeyboardLayout(isShift ? "default" : "shift");
      return;
    }

    if (button === "{tab}") {
      toggleLanguageMode();
      return;
    }

    if (button === "{bksp}") {
      if (start > 0 && start === end) {
        const beforeCursor = currentText.substring(0, start - 1);
        const afterCursor = currentText.substring(end);
        const newText = beforeCursor + afterCursor;
        return applyTextChange(newText, Math.max(0, start - 1));
      } else if (start !== end) {
        const before = currentText.substring(0, start);
        const after = currentText.substring(end);
        const newText = before + after;
        return applyTextChange(newText, start);
      }
      return;
    }

    if (button === "{enter}") {
      const before = currentText.substring(0, start);
      const after = currentText.substring(end);
      const newText = before + "\n" + after;
      return applyTextChange(newText, start + 1);
    }

    if (button === "{space}") {
      const before = currentText.substring(0, start);
      const after = currentText.substring(end);
      const newText = before + " " + after;
      return applyTextChange(newText, start + 1);
    }

    // Handle regular characters
    if (button && button.length === 1) {
      const before = currentText.substring(0, start);
      const after = currentText.substring(end);
      const newText = before + button + after;
      return applyTextChange(newText, start + button.length);
    }

    // Handle special keyboard buttons
    switch (button) {
      case ".com":
        const beforeDot = currentText.substring(0, start);
        const afterDot = currentText.substring(end);
        const textWithDot = beforeDot + ".com" + afterDot;
        return applyTextChange(textWithDot, start + 4);
      case "@":
        const beforeAt = currentText.substring(0, start);
        const afterAt = currentText.substring(end);
        const textWithAt = beforeAt + "@" + afterAt;
        return applyTextChange(textWithAt, start + 1);
      default:
        break;
    }
  }, [activeTextObject, cursorPosition, updateActiveTextObject, safeSetSelectionRange, captureSnapshot, isShift, toggleLanguageMode]);

  const truncateText = useCallback((text, maxLength) => {
    if (!text) return "Empty";
    if (text.length > maxLength) return text.substring(0, maxLength) + "...";
    return text;
  }, []);

  const getFontDropdownTitle = useCallback(() => {
    const truncated = truncateText(activeTextObject?.text || "", 30);
    if (truncated !== "Empty") {
      return <span style={{ fontFamily: activeTextObject?.fontFamily }}>{truncated}</span>;
    }
    return "Select font";
  }, [activeTextObject, truncateText]);

  const calculateTotalPrice = useCallback(() => {
    return (textObjects || []).reduce((total, obj) => {
      const textLength = obj.text ? obj.text.replaceAll(" ", "").replaceAll("\n", "").length : 0;
      return total + textLength * 1.75;
    }, 0);
  }, [textObjects]);

  const calculateCurrentPrice = useCallback(() => {
    const textLength = activeTextObject?.text ? activeTextObject.text.replaceAll(" ", "").replaceAll("\n", "").length : 0;
    return textLength * 1.75;
  }, [activeTextObject]);

  // focus textarea when switching active object
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      setTimer(() => {
        try {
          ta.focus();
        } catch (err) {}
      }, 10);
    }
  }, [activeTextObject?.id]);

  // Get keyboard layout configuration
  const getKeyboardLayout = useMemo(() => {
    if (isHebrewMode) {
      return {
        default: [
          " 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} / ' ק ר א ט ו ן ם פ ] [ :",
          "{lock} ש ד ג כ ע י ח ל ך ף , {enter}",
          "{shift} ז ס ב ה נ מ צ ת ץ . {shift}",
          ".com @ {space}"
        ],
        shift: [
          "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P { } |",
          '{lock} A S D F G H J K L : " {enter}',
          "{shift} Z X C V B N M < > ? {shift}",
          ".com @ {space}"
        ]
      };
    } else {
      return {
        default: [
          "` 1 2 3 4 5 6 7 8 9 0 - = {bksp}",
          "{tab} q w e r t y u i o p [ ] \\",
          "{lock} a s d f g h j k l ; ' {enter}",
          "{shift} z x c v b n m , . / {shift}",
          ".com @ {space}"
        ],
        shift: [
          "~ ! @ # $ % ^ & * ( ) _ + {bksp}",
          "{tab} Q W E R T Y U I O P { } |",
          '{lock} A S D F G H J K L : " {enter}',
          "{shift} Z X C V B N M < > ? {shift}",
          ".com @ {space}"
        ]
      };
    }
  }, [isHebrewMode]);
  // Highlight color for active text object
  const getTextObjectStyle = useCallback((obj) => {
    const baseStyle = {
      cursor: "pointer",
      padding: "10px",
      marginBottom: "8px",
      borderRadius: "8px",
      transition: "all 0.3s ease",
    };

    if (obj.isActive) {
      return {
        ...baseStyle,
        backgroundColor: "#e3f2fd",
        borderLeft: "4px solid #2196f3",
        boxShadow: "0 2px 4px rgba(33, 150, 243, 0.2)",
        transform: "translateX(4px)",
      };
    }

    return {
      ...baseStyle,
      backgroundColor: "#f8f9fa",
      borderLeft: "4px solid transparent",
      border: "1px solid #dee2e6",
    };
  }, []);

  return (
    <>
      <div className="embroider-container" style={{
        backgroundColor: "#E8E9ED",
        borderRadius: "10px",
      }}>
        <div className="multi-text-management mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">Text Objects ({(textObjects || []).length})</h5>
            <Button variant="success" size="sm" onClick={addTextObject} title="Add New Text Object">
              <i className="fas fa-plus me-1"></i> Add Text
            </Button>
          </div>

          <div className="text-objects-list mb-3">
            {(textObjects || []).map((obj, index) => (
              <div
                key={obj.id}
                className={`text-object-item ${obj.isActive ? "active" : ""}`}
                onClick={() => switchTextObject(obj.id)}
                style={getTextObjectStyle(obj)}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="position-relative me-2">
                      <span className={`text-object-number ${obj.isActive ? "active" : ""}`}>
                        #{index + 1}
                      </span>
                      {obj.isActive && (
                        <div className="active-indicator" style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#4CAF50",
                          borderRadius: "50%",
                          border: "2px solid white",
                        }} />
                      )}
                    </div>
                    <div className="d-flex flex-column">
                      <span className="text-object-preview fw-medium">
                        {truncateText(obj.text || `Text ${index + 1}`, 20)}
                      </span>
                      <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                        {obj.fontFamily.split(",")[0]} • {obj.fontSize}px • {obj.shape}
                      </small>
                    </div>
                  </div>
                  <div className="text-object-actions">
                    <span className="badge bg-secondary me-2">{obj.shape}</span>
                    {(textObjects || []).length > 1 && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteTextObject(obj.id);
                        }}
                        title="Delete Text Object"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="language-toggle-section" style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "15px", 
          padding: "10px", 
          backgroundColor: "#f8f9fa", 
          borderRadius: "8px", 
          border: "1px solid #dee2e6" 
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontWeight: "bold", color: "#495057" }}>Language Mode:</span>
            <button 
              className={`btn ${isHebrewMode ? "btn-primary" : "btn-outline-primary"}`} 
              onClick={toggleLanguageMode} 
              style={{ 
                padding: "8px 16px", 
                borderRadius: "20px", 
                fontSize: "14px", 
                fontWeight: "bold", 
                transition: "all 0.3s ease",
                direction: "ltr" // Force LTR for the button text
              }}
            >
              {isHebrewMode ? "🇮🇱 עברית (RTL)" : "🇺🇸 English (LTR)"}
            </button>
          </div>
          <div style={{ 
            fontSize: "12px", 
            color: "#6c757d", 
            fontStyle: "italic",
            direction: "ltr" // Force LTR for this text
          }}>
            {isHebrewMode ? "Hebrew keyboard - Typing from right to left" : "English keyboard - Typing from left to right"}
          </div>
        </div>

        <div className="editor-header">
          <div className="text-editor-toolbar">
            <div className="text-editor-toolbar__wrapper">
              <div className="text-editor-toolbar__item" title="Letter Spacing">
                <div className="text-editor-toolbar__control-group">
                  <span className="text-editor-toolbar__icon"><i className="fas fa-text-width"></i></span>
                  <input
                    type="range"
                    className="text-editor-toolbar__slider"
                    value={activeTextObject?.letterSpacing || 0}
                    onChange={(e) => {
                      updateActiveTextObject({ letterSpacing: parseInt(e.target.value, 10) });
                      setTimer(() => captureSnapshot(), 100);
                    }}
                    min={0}
                    max={30}
                  />
                  <span className="text-editor-toolbar__value">{activeTextObject?.letterSpacing || 0}px</span>
                </div>
              </div>

              {activeTextObject?.shape === "straight" && (
                <div className="text-editor-toolbar__item" title="Line Height">
                  <div className="text-editor-toolbar__control-group">
                    <span className="text-editor-toolbar__icon"><i className="fas fa-arrows-alt-v"></i></span>
                    <input
                      type="range"
                      className="text-editor-toolbar__slider"
                      value={activeTextObject?.lineHeight || 1}
                      onChange={(e) => {
                        updateActiveTextObject({ lineHeight: parseFloat(e.target.value) });
                        setTimer(() => captureSnapshot(), 100);
                      }}
                      min={0}
                      max={10}
                      step={0.1}
                    />
                    <span className="text-editor-toolbar__value">{activeTextObject?.lineHeight || 1}</span>
                  </div>
                </div>
              )}

              {(activeTextObject?.shape === "arch" || activeTextObject?.shape === "circle") && (
                <div className="text-editor-toolbar__item" title="Radius">
                  <div className="text-editor-toolbar__control-group">
                    <span className="text-editor-toolbar__icon"><i className="fas fa-bullseye h2 m-0" style={{ position: "relative", top: 2 }}></i></span>
                    <input
                      type="range"
                      className="text-editor-toolbar__slider"
                      value={activeTextObject?.radius || 0}
                      onChange={(e) => {
                        updateActiveTextObject({ radius: parseInt(e.target.value, 10) });
                        setTimer(() => captureSnapshot(), 100);
                      }}
                      min={0}
                      max={100}
                    />
                    <span className="text-editor-toolbar__value">{activeTextObject?.radius || 0}px</span>
                  </div>
                </div>
              )}

              <div className="text-editor-toolbar__item text-editor-toolbar__text-controls p-0">
                <button className="text-editor-toolbar__btn m-0 py-0 px-3" onClick={toggleKeyboard} title="Toggle Keyboard">
                 <img loading="lazy" src={keyboardIcon} alt="keyboard" style={{ width: "45px", height: "40px", backgroundColor: "transparent" }} className="text-editor-toolbar__keyboard-icon" />
                </button>
                <div className="text-editor-toolbar__font-size-controls">
                  <button className="text-editor-toolbar__btn px-3" onClick={decreaseFontSize} title="Decrease Font Size">
                    <i className="fas fa-search-minus"></i>
                  </button>
                  <button className="text-editor-toolbar__btn px-3" onClick={increaseFontSize} title="Increase Font Size">
                    <i className="fas fa-search-plus"></i>
                  </button>
                </div>
                
                {/* Alignment buttons - Added here */}
                <div className="text-editor-toolbar__alignment-controls ms-2 d-flex align-items-center">
                  <button 
                    className={`text-editor-toolbar__btn px-3 ${activeTextObject?.textAlign === 'left' ? 'active' : ''}`} 
                    onClick={() => {
                      updateActiveTextObject({ textAlign: 'left' });
                      setTimer(() => captureSnapshot(), 100);
                    }}
                    title="Align Left"
                  >
                    <i className="fas fa-align-left"></i>
                  </button>
                  <button 
                    className={`text-editor-toolbar__btn px-3 ${activeTextObject?.textAlign === 'center' ? 'active' : ''}`} 
                    onClick={() => {
                      updateActiveTextObject({ textAlign: 'center' });
                      setTimer(() => captureSnapshot(), 100);
                    }}
                    title="Align Center"
                  >
                    <i className="fas fa-align-center"></i>
                  </button>
                  <button 
                    className={`text-editor-toolbar__btn px-3 ${activeTextObject?.textAlign === 'right' ? 'active' : ''}`} 
                    onClick={() => {
                      updateActiveTextObject({ textAlign: 'right' });
                      setTimer(() => captureSnapshot(), 100);
                    }}
                    title="Align Right"
                  >
                    <i className="fas fa-align-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Form.Control
          ref={textareaRef}
          as="textarea"
          rows={3}
          onChange={handleTextareaChange}
          onSelect={handleTextareaSelect}
          onKeyUp={handleTextareaSelect}
          value={activeTextObject?.text || ""}
          autoFocus
          className="embroider-textarea"
          placeholder={isHebrewMode ? "כתוב טקסט כאן..." : "Write text here..."}
          style={{
            direction: isHebrewMode && isRtl ? "rtl" : "ltr",
            textAlign: activeTextObject?.textAlign || (isHebrewMode && isRtl ? "right" : "left"), // Updated this line
            border: "2px solid #2196f3",
            boxShadow: "0 0 0 0.2rem rgba(33, 150, 243, 0.25)",
            fontFamily: activeTextObject?.fontFamily,
            fontSize: `${activeTextObject?.fontSize || 48}px`,
            color: activeTextObject?.color || "#FFD700",
          }}
        />

        <div className="d-flex align-items-center mt-3 mb-2">
          <label htmlFor="font-fmly" className="m-0 p-0 h4 fw-bold me-3">Select Font Family</label>
          <span className="badge bg-info me-2">Current: {activeTextObject?.fontFamily?.split(",")[0] || "Baziporen"}</span>
        </div>
        
        <DropdownButton 
          title={getFontDropdownTitle()} 
          onSelect={handleFontFamilyChange} 
          variant="primary" 
          style={{ fontFamily: activeTextObject?.fontFamily }} 
          className="font-selector mb-3" 
          id="font-fmly"
        >
          <div className="font-list">
            {fontFamilyList.map((font, index) => (
              <Dropdown.Item 
                key={index} 
                eventKey={font} 
                style={{ 
                  fontFamily: font, 
                  fontSize: "24px",
                  backgroundColor: font === activeTextObject?.fontFamily ? "#e3f2fd" : "transparent",
                  borderLeft: font === activeTextObject?.fontFamily ? "4px solid #2196f3" : "none",
                  direction: isHebrewMode ? "rtl" : "ltr",
                }}
                active={font === activeTextObject?.fontFamily}
              >
                <div className="d-flex align-items-center">
                  <span>{activeTextObject?.text || font}</span>
                  {font === activeTextObject?.fontFamily && (
                    <i className="fas fa-check ms-auto text-success"></i>
                  )}
                </div>
              </Dropdown.Item>
            ))}
          </div>
        </DropdownButton>

        <div className="color-section">
          <div className="section-title">Color</div>
          <div className="price-display">
            Total Price: ${calculateTotalPrice().toFixed(2)}
            {(textObjects || []).length > 1 && (
              <small className="text-muted ms-2">(${calculateCurrentPrice().toFixed(2)} for current)</small>
            )}
          </div>
        </div>

        <div className="color-palette">
          {(colorList || [])
            .filter((color) => {
              if (Array.isArray(color.color_code) && color.color_code.length === 1) return true;
              if (typeof color.color_code === "string" && color.color_code.trim() !== "") return true;
              return false;
            })
            .map((color, index) => {
              const background = Array.isArray(color.color_code) ? color.color_code[0] : color.color_code;
              const isActive = activeTextObject?.color === color.color_code;
              console.log("selectedColor",color)
              return (
                <div
                  key={index}
                  className="color-swatch-wrapper"
                  onClick={() => {
                    updateActiveTextObject({ color: color.color_code ,color_name:color?.color_name,code:color?.code });
                    setTimer(() => captureSnapshot(), 100);
                  }}
                >
                  <div
                    className="color-swatch"
                    style={{
                      background,
                      border: isActive ? "3px solid #007bff" : "1px solid #ddd",
                      boxShadow: isActive ? "0 0 0 3px rgba(0, 123, 255, 0.2)" : "none",
                      transform: isActive ? "scale(1.1)" : "scale(1)",
                      transition: "all 0.2s ease",
                      position: "relative",
                    }}
                  >
                    {isActive && (
                      <div style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        backgroundColor: "#007bff",
                        color: "white",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}>
                        ✓
                      </div>
                    )}
                  </div>
                  <span className="color-swatch-name">
                    {color.color_name || `Color ${index + 1}`}
                  </span>
                </div>
              );
            })}
        </div>


        <div className="action-buttons">
          <div className="w-100 d-flex flex-wrap gap-2" >
           
            <button className={`shape-btn ${activeTextObject?.shape === "straight" ? "active" : ""}`} onClick={() => { updateActiveTextObject({ shape: "straight" }); setTimer(() => captureSnapshot(), 100); }}>
              <i className="fas fa-grip-lines"></i> Straight
            </button>
            <button className={`shape-btn ${activeTextObject?.shape === "arch" ? "active" : ""}`} onClick={() => { updateActiveTextObject({ shape: "arch", radius: 80 }); setTimer(() => captureSnapshot(), 100); }}>
              <i className="fas fa-rainbow"></i> Arch
            </button>
            
            <button className={`shape-btn ${activeTextObject?.shape === "circle" ? "active" : ""}`} onClick={() => { updateActiveTextObject({ shape: "circle", radius: 40 }); setTimer(() => captureSnapshot(), 100); }}>
              <i className="far fa-circle"></i> Circle
            </button>
            <button className="clear-btn" onClick={handleClearText}><i className="fas fa-eraser"></i> Clear Current</button>
            {(textObjects || []).length > 1 && <button className="clear-btn btn-danger" onClick={handleClearAll}><i className="fas fa-trash"></i> Clear All</button>}
            
          </div>
          <div className="letter-count">
            <span>Total Letters: </span>
            <span className="count-value">{(textObjects || []).reduce((total, obj) => total + ((obj.text || "").replace(/\s+/g, "").length), 0)}</span>
            {(textObjects || []).length > 1 && <small className="text-muted ms-2">({(activeTextObject?.text || "").replace(/\s+/g, "").length || 0} in current)</small>}
          </div>
        </div>
      </div>

      {showKeyboard && (
        <div className="modal fade show bottom-center modal-xl" style={{ display: "block", paddingRight: "15px" }} aria-labelledby="keyboardModalLabel" aria-hidden="true" onClick={(e) => { if (e.target.classList.contains("modal")) toggleKeyboard(); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="keyboardModalLabel">Virtual Keyboard</h5>
                <button type="button" className="btn-close" onClick={toggleKeyboard} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <div className={`keyboard-container ${keyboardTheme}`} ref={keyboardRef} style={{ cursor: "default", zIndex: 1000, width: "900px", maxWidth: "90vw" }}>
                  <div className="keyboard-header">
                    <h3>{isHebrewMode ? "עברית Keyboard (RTL)" : "English Keyboard (LTR)"}</h3>
                    <div className="theme-toggle" onClick={() => setKeyboardTheme((t) => (t === "light" ? "dark" : "light"))}>
                      {keyboardTheme === "light" ? "🌙" : "☀️"}
                    </div>
                  </div>

                  <Keyboard
                    onChange={() => {}}
                    onKeyPress={onKeyPress}
                    useMouseEvents={true}
                    newLineOnEnter={false}
                    disableCaretPositioning={true}
                    layout={getKeyboardLayout}
                    layoutName={isShift ? "shift" : "default"}
                    rtl={isHebrewMode}
                    theme={`hg-theme-default ${keyboardTheme}`}
                    buttonTheme={[
                      { class: "hg-activeKey", buttons: activeKey || "" },
                      { class: "hg-functionKey", buttons: "{shift} {lock} {tab} {bksp} {enter}" },
                      { class: "hg-spaceKey", buttons: "{space}" },
                      ...(isHebrewMode ? [{ class: "hg-hebrewKey", buttons: "ק ר א ט ו ן ם פ ש ד ג כ ע י ח ל ך ף ז ס ב ה נ מ צ ת ץ" }] : []),
                    ]}
                    display={{
                      "{bksp}": "⌫",
                      "{enter}": "↵ Enter",
                      "{space}": isHebrewMode ? "רווח" : "Space",
                      "{tab}": isHebrewMode ? "⇄ Tab" : "⇄ Tab",
                      "{lock}": "⇪ Caps",
                      "{shift}": "⇧ Shift",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Personalize;
